import React, { useState } from 'react';

import { usePayouts, useReviewPayout } from '../../../hooks/usePayouts';
import { showToast } from '../../../utils/toast';
import { AdminPayoutsView } from './AdminPayouts.view';

/**
 * Payouts, the container. Owns the query, the status filter, pagination and the
 * approve/reject mutations. `AdminPayouts.view.jsx` holds everything visual and
 * is pure.
 *
 * BEHAVIOUR PRESERVED
 * -------------------
 * Same `usePayouts` query and same params, same `useReviewPayout` mutation for
 * both actions, same reset-to-first-page on a status change, same client-side
 * re-filter over the returned page (the server filter is sent AND the result is
 * filtered again, exactly as before, because the endpoint does not reliably
 * honour `status`), same optimistic local status update.
 *
 * TWO THINGS CHANGED, BOTH DELIBERATE
 * -----------------------------------
 * 1. Toasts go through `utils/toast` like the rest of the app instead of
 *    importing `react-toastify` directly. Same messages.
 * 2. Sensitive data is never logged. The two `console.error` calls printed the
 *    caught error, which for a failed payout review carries the payout id and
 *    the response body. Failures surface as a toast only.
 *
 * `index` on this endpoint is a zero-based PAGE number, which is how the old
 * `PaginationControls` used it. `/admin-sellers` sends an item offset for the
 * same parameter name against a different endpoint; that discrepancy is a
 * backend question and is reported rather than harmonised here.
 */
const PAGE_SIZE = 10;

const AdminPayouts = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');

  const [selected, setSelected] = useState(null);
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const {
    data: payoutsData,
    isLoading,
    error,
    refetch,
  } = usePayouts({
    index: pageIndex,
    offset: PAGE_SIZE,
    ...(statusFilter ? { status: statusFilter } : {}),
  });

  const reviewPayout = useReviewPayout();

  const payouts = payoutsData?.results || [];
  const displayed = statusFilter
    ? payouts.filter((row) => (row.status || '').toLowerCase() === statusFilter)
    : payouts;

  const handleApprove = async () => {
    if (!selected?.id) return;
    try {
      await reviewPayout.mutateAsync({ payout_id: selected.id, action: 'approve' });
      showToast.success('Payout approved successfully!');
      setSelected((prev) => (prev ? { ...prev, status: 'approved' } : prev));
      setConfirmApproveOpen(false);
    } catch (caught) {
      // Never logged: a failed payout review carries the payout id and the
      // response body, and both are sensitive.
      showToast.error(caught?.message || 'Failed to approve payout');
    }
  };

  const handleReject = async (reason) => {
    if (!selected?.id) return;
    try {
      await reviewPayout.mutateAsync({
        payout_id: selected.id,
        action: 'reject',
        reason,
      });
      showToast.success('Payout rejected successfully!');
      setSelected((prev) => (prev ? { ...prev, status: 'rejected' } : prev));
      setRejectOpen(false);
    } catch (caught) {
      showToast.error(caught?.message || 'Failed to reject payout');
    }
  };

  const total = payoutsData?.total || 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <AdminPayoutsView
      payouts={displayed}
      loading={isLoading}
      error={error}
      onRetry={refetch}
      status={statusFilter}
      onStatusChange={(value) => {
        setStatusFilter(value);
        setPageIndex(0);
      }}
      page={pageIndex + 1}
      pageCount={pageCount}
      pageSize={PAGE_SIZE}
      total={total}
      onPageChange={(next) => setPageIndex(next - 1)}
      selected={selected}
      onOpen={setSelected}
      onClose={() => setSelected(null)}
      onApprove={handleApprove}
      onReject={handleReject}
      actionBusy={reviewPayout.isPending}
      confirmApproveOpen={confirmApproveOpen}
      onConfirmApproveOpen={() => setConfirmApproveOpen(true)}
      onConfirmApproveClose={() => setConfirmApproveOpen(false)}
      rejectOpen={rejectOpen}
      onRejectOpen={() => setRejectOpen(true)}
      onRejectClose={() => setRejectOpen(false)}
    />
  );
};

export default AdminPayouts;
